import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, Link, useNavigate } from 'react-router-dom';

const VenueDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [venue, setVenue] = useState<any>(null);
  const [showModal, setShowModal] = useState(false);
  const [editData, setEditData] = useState({
    name: '',
    address: '',
    contactName: '',
    phoneNumber: '',
    pastEventsBase: '0',
    notes: ''
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchVenue();
  }, [id]);

  const fetchVenue = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/Venues/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const venueData = response.data;
      setVenue(venueData);
      setEditData({
        name: venueData.name,
        address: venueData.address,
        contactName: venueData.contactName || '',
        phoneNumber: venueData.phoneNumber || '',
        pastEventsBase: venueData.pastEventsBase.toString(),
        notes: venueData.notes || ''
      });
    } catch (error: any) {
      console.error('Failed to fetch venue:', error);
      setError('Failed to load venue. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateVenue = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editData.name || !editData.address) {
      setError('Name and Address are required.');
      return;
    }
    if (parseInt(editData.pastEventsBase) < 0) {
      setError('Past Events Base count cannot be negative.');
      return;
    }
    setError('');
    setSuccess('');
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/Venues/${id}`,
        {
          name: editData.name,
          address: editData.address,
          contactName: editData.contactName,
          phoneNumber: editData.phoneNumber,
          pastEventsBase: parseInt(editData.pastEventsBase),
          notes: editData.notes
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowModal(false);
      setSuccess('Venue updated successfully!');
      fetchVenue();
    } catch (error: any) {
      console.error('Failed to update venue:', error);
      setError('Failed to update venue. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!venue) return <div className="container mt-5">{isLoading ? 'Loading...' : error || 'Loading...'}</div>;

  return (
    <div className="container mt-5">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>{venue.name}</h2>
        <button
          className="btn btn-outline-secondary"
          onClick={() => navigate('/venues')}
          disabled={isLoading}
        >
          Back to Venues
        </button>
      </div>
      {error && <div className="alert alert-danger">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}
      {isLoading && <div className="alert alert-info">Loading...</div>}
      <div className="card shadow-sm mb-4">
        <div className="card-header bg-primary text-white">
          <h4 className="mb-0">Venue Details</h4>
        </div>
        <div className="card-body">
          <p><strong>Address:</strong> {venue.address}</p>
          <p><strong>Contact Name:</strong> {venue.contactName || 'N/A'}</p>
          <p><strong>Phone Number:</strong> {venue.phoneNumber || 'N/A'}</p>
          <p><strong>Past Events Base:</strong> {venue.pastEventsBase}</p>
          <p><strong>Total Past Events:</strong> {venue.pastEvents}</p>
          <p><strong>Upcoming Events:</strong> {venue.upcomingEvents}</p>
          <p><strong>Notes:</strong> {venue.notes || 'N/A'}</p>
          <button
            className="btn btn-outline-primary mb-3"
            onClick={() => setShowModal(true)}
            disabled={isLoading}
          >
            Edit Venue
          </button>
        </div>
      </div>

      {/* Modal */}
      <div
        className={`modal fade ${showModal ? 'show' : ''}`}
        style={{ display: showModal ? 'block' : 'none' }}
        id="editVenueModal"
        tabIndex={-1}
        aria-labelledby="editVenueModalLabel"
        aria-hidden={!showModal}
      >
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="editVenueModalLabel">Edit Venue</h5>
              <button
                type="button"
                className="btn-close"
                onClick={() => setShowModal(false)}
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleUpdateVenue}>
                <div className="mb-3">
                  <label className="form-label">Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={editData.name}
                    onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                    disabled={isLoading}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Address</label>
                  <input
                    type="text"
                    className="form-control"
                    value={editData.address}
                    onChange={(e) => setEditData({ ...editData, address: e.target.value })}
                    disabled={isLoading}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Contact Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={editData.contactName}
                    onChange={(e) => setEditData({ ...editData, contactName: e.target.value })}
                    disabled={isLoading}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Phone Number</label>
                  <input
                    type="text"
                    className="form-control"
                    value={editData.phoneNumber}
                    onChange={(e) => setEditData({ ...editData, phoneNumber: e.target.value })}
                    disabled={isLoading}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Past Events Base (pre-app events)</label>
                  <input
                    type="number"
                    className="form-control"
                    value={editData.pastEventsBase}
                    onChange={(e) => setEditData({ ...editData, pastEventsBase: e.target.value })}
                    disabled={isLoading}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Notes</label>
                  <textarea
                    className="form-control"
                    value={editData.notes}
                    onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
                    disabled={isLoading}
                    rows={3}
                    placeholder="Enter notes (optional)"
                  />
                </div>
                <div className="d-flex justify-content-end">
                  <button
                    type="submit"
                    className="btn btn-primary me-2"
                    disabled={isLoading}
                  >
                    {isLoading ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowModal(false)}
                    disabled={isLoading}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      {showModal && (
        <div
          className="modal-backdrop fade show"
          onClick={() => setShowModal(false)}
        ></div>
      )}
    </div>
  );
};

export default VenueDetail;